/**
 * Ticker_Operacional — componente de presentación del flujo continuo y
 * ordenado de Eventos_Operacionales en tiempo real (Glosario; R7.4).
 *
 * Refleja en la UI los eventos entregados por el Realtime Hub mediante
 * **APPEND INCREMENTAL** en orden temporal no decreciente (R7.4): los
 * eventos nuevos se muestran sin recomputar la lista completa.
 *
 * Degradación de la Suscripcion_Realtime (R1.5, R1.9): cuando el canal se
 * interrumpe, el Ticker muestra un aviso de **"estados posiblemente
 * obsoletos"** y conserva en pantalla los últimos eventos conocidos en
 * lugar de vaciarse; ofrece reanudar la entrega (resume/re-sync, R7.7/8).
 *
 * Es un componente reutilizable y de presentación: obtiene su estado del
 * hook {@link useRealtime} (que consume el Hub vía el runtime selector).
 * El cableado a las vistas concretas (LiveOperations, OperationalMap,
 * AlertCenter) corresponde a la tarea 5.5.
 */
import { useMemo } from "react";

import type { EventoOperacional, FiltroRealtime } from "../api/contracts";
import { useRealtime, type UseRealtimeState } from "../system/useRealtime";
import { Icon } from "./Icon";

/** Variante visual del Ticker. */
export type TickerVariant = "panel" | "compact";

export interface TickerProps {
  /** Filtro de la Suscripcion_Realtime (tipos de evento o entidades) (R7.2). */
  filtro: FiltroRealtime;
  /** Título del panel. Default "Ticker operacional". */
  titulo?: string;
  /** Máximo de eventos visibles. Default 50. */
  maxVisibles?: number;
  /** Variante de presentación. Default "panel". */
  variant?: TickerVariant;
  /**
   * Estado de realtime externo. Si se omite, el Ticker abre su propia
   * suscripción con {@link useRealtime}. Permite compartir un único canal
   * entre varias superficies (Ticker + mapa) en la tarea 5.5.
   */
  realtime?: UseRealtimeState;
}

/** Tono de la badge según la familia del tipo de evento. */
function tonoEvento(tipo: string): string {
  if (tipo.startsWith("alerta")) return "badge--danger";
  if (tipo.startsWith("anomalia")) return "badge--warn";
  if (tipo.startsWith("caso")) return "badge--accent";
  if (tipo.startsWith("valvula") || tipo.startsWith("prv")) return "badge--info";
  return "badge--neutral";
}

/** Formatea el instante de un evento como hora local legible. */
function formatHora(ts: string): string {
  const t = Date.parse(ts);
  if (!Number.isFinite(t)) return ts;
  return new Date(t).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Resumen legible de un Evento_Operacional (tipo + entidad). */
function resumenEvento(evento: EventoOperacional): string {
  return `${evento.entidad_ref.tipo} ${evento.entidad_ref.id}`;
}

export function Ticker({
  filtro,
  titulo = "Ticker operacional",
  maxVisibles = 50,
  variant = "panel",
  realtime,
}: TickerProps): JSX.Element {
  // Usa el estado provisto o abre una suscripción propia.
  const propio = useRealtime(filtro, { enabled: realtime === undefined });
  const rt = realtime ?? propio;

  const { eventos, obsoleto, conectado, error, reconectar } = rt;

  // Append incremental: los más recientes primero, acotados a maxVisibles (R7.4).
  const visibles = useMemo(() => {
    const recientes = eventos.slice(-maxVisibles);
    return [...recientes].reverse();
  }, [eventos, maxVisibles]);

  const compact = variant === "compact";

  return (
    <section className="card" data-ticker data-obsoleto={obsoleto} aria-live="polite">
      <header
        className="hstack"
        style={{ justifyContent: "space-between", marginBottom: 10, gap: 8 }}
      >
        <div className="hstack" style={{ gap: 8 }}>
          <span className="section__icon">
            <Icon name="bell" size={14} />
          </span>
          <strong style={{ fontSize: 13, color: "var(--c-text-strong)" }}>{titulo}</strong>
          <span
            className={`badge ${conectado && !obsoleto ? "badge--accent" : "badge--neutral"}`}
            title={conectado ? "Suscripción activa" : "Suscripción inactiva"}
          >
            <span
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: 999,
                marginRight: 5,
                background:
                  conectado && !obsoleto ? "var(--c-success)" : "var(--c-text-faint)",
              }}
            />
            {conectado && !obsoleto ? "EN VIVO" : "PAUSADO"}
          </span>
        </div>
        <span className="text-faint text-mono" style={{ fontSize: 11 }}>
          {eventos.length} evento{eventos.length === 1 ? "" : "s"}
        </span>
      </header>

      {/* Filtro inválido u otro error de suscripción (R7.3). */}
      {error && (
        <div className="status-block status-block--alert" style={{ marginBottom: 10 }}>
          <Icon name="shield" size={14} />
          <span style={{ fontSize: 12 }}>{error}</span>
        </div>
      )}

      {/* Degradación: estados posiblemente obsoletos (R1.9). */}
      {obsoleto && !error && (
        <div
          className="status-block status-block--info"
          style={{ marginBottom: 10, justifyContent: "space-between" }}
        >
          <span className="hstack" style={{ gap: 6, fontSize: 12 }}>
            <Icon name="clock" size={14} />
            Suscripción interrumpida — los estados mostrados pueden estar obsoletos. Se preservan
            los últimos estados conocidos.
          </span>
          <button type="button" className="btn btn--ghost" onClick={reconectar}>
            Reanudar
          </button>
        </div>
      )}

      {visibles.length === 0 ? (
        <p className="text-faint" style={{ fontSize: 12.5, margin: "8px 0" }}>
          Sin eventos operacionales recientes.
        </p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: compact ? 4 : 6,
            maxHeight: compact ? 220 : 420,
            overflowY: "auto",
            // Atenúa el flujo cuando los estados son posiblemente obsoletos (R1.9).
            opacity: obsoleto ? 0.6 : 1,
            transition: "opacity 160ms ease",
          }}
        >
          {visibles.map(({ evento, seq_global }) => (
            <li
              key={`${evento.event_id}-${seq_global}`}
              className="hstack"
              style={{
                gap: 8,
                padding: compact ? "4px 6px" : "6px 8px",
                borderRadius: "var(--radius-md)",
                background: "var(--c-surface-2)",
                border: "1px solid var(--c-border)",
              }}
            >
              <span className="text-mono text-faint" style={{ fontSize: 10.5, minWidth: 58 }}>
                {formatHora(evento.ts)}
              </span>
              <span className={`badge ${tonoEvento(evento.tipo)}`} style={{ fontSize: 10 }}>
                {evento.tipo}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "var(--c-text)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={resumenEvento(evento)}
              >
                {resumenEvento(evento)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
